import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import { dirFor } from '@/i18n';
import {
  useConfig,
  useDepthChart,
  useOrg,
  usePlayers,
  useSaveSquadSheet,
  useSquadSheet,
} from '@/app/queries';
import { useSession as useAuthSession } from '@/app/session';
import { formatDate, sheetName } from '@/lib/format';
import {
  applyPlacements,
  buildSquadSheet,
  type CoverMap,
  type DepthOrder,
  type SheetPlayer,
} from '@/lib/squadSheet';
import { drawSquadSheet, type SquadSheet } from '@/diagrams/squadCanvas';
import { FORMATIONS, findFormation } from '@shared/formations';
import { todayISO } from '@shared/dates';
import { Button, Spinner } from '@/ui/Button';
import { SelectInput } from '@/ui/Field';
import { EmptyState, Notice, Panel } from '@/ui/Panel';

/** A4 portrait at a size that stays crisp when rasterised into a PDF. */
const SHEET_WIDTH = 1240;
const SHEET_HEIGHT = Math.round((SHEET_WIDTH * 297) / 210);

/**
 * The whole squad, standing where they play.
 *
 * The formation sheet answers "who starts". This answers "what have I got" —
 * one page a coach can hand to a director, a goalkeeping coach or a parent
 * without explaining it first.
 */
export function SquadPitch({ locale, teamId }: { locale: Locale; teamId: string }) {
  const t = useTranslations();
  const { user } = useAuthSession();
  const org = useOrg();
  const players = usePlayers(teamId);
  const depth = useDepthChart(teamId);
  const config = useConfig();
  const arrangement = useSquadSheet(teamId);
  const saveArrangement = useSaveSquadSheet(teamId);

  const [formationKey, setFormationKey] = useState('4-2-3-1');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const canvas = useRef<HTMLCanvasElement>(null);

  // The chart's own shape is the one the coach plays, so it is the right default.
  useEffect(() => {
    const key = depth.data?.chart?.formationKey;
    if (key) setFormationKey(key);
  }, [depth.data?.chart?.formationKey]);

  const squad: SheetPlayer[] = useMemo(
    () =>
      (players.data ?? []).map((p) => ({
        id: p.id,
        name: sheetName(p.knownAs ?? p.firstName, p.lastName),
        shirtNumber: p.shirtNumber,
        primaryPositionKey: p.primaryPositionKey,
        secondaryPositionKey: p.secondaryPositionKey,
        roleKey: p.roleKey,
      })),
    [players.data],
  );

  const ranked: DepthOrder = useMemo(() => {
    const out: DepthOrder = new Map();
    for (const slot of depth.data?.slots ?? []) {
      const ids = [...slot.entries].sort((a, b) => a.rank - b.rank).map((e) => e.playerId);
      if (ids.length > 0) out.set(slot.positionKey, ids);
    }
    return out;
  }, [depth.data?.slots]);

  // Which positions cover which is the club's configuration, not this file's
  // idea of football.
  const covers: CoverMap = useMemo(() => {
    const out: CoverMap = new Map();
    for (const pos of config.data?.positions ?? []) {
      if (pos.covers?.length) out.set(pos.key, pos.covers);
    }
    return out;
  }, [config.data?.positions]);

  /*
    The coach's own placements, over the arrangement the app worked out.

    The sheet is drawn from the depth chart and falls back to each player's own
    position — a good guess, and still a guess, on a page that goes to a
    director. Asked which way round he wanted it, he was clear: the sheet keeps
    its own arrangement and the depth chart is left alone, so these two are
    allowed to disagree.
  */
  const placements = arrangement.data?.placements ?? {};
  const laid = useMemo(
    () => applyPlacements(squad, ranked, placements),
    [squad, ranked, arrangement.data?.placements],
  );

  const built = useMemo(
    () => buildSquadSheet(formationKey, laid.players, laid.ranked, covers),
    [formationKey, laid, covers],
  );

  /** The positions this shape has, which are the only ones the sheet can draw. */
  const positions = useMemo(
    () => [...new Set((findFormation(formationKey)?.slots ?? []).map((s) => s.positionKey))],
    [formationKey],
  );

  const place = (playerId: string, positionKey: string) => {
    const next = { ...placements };
    if (positionKey) next[playerId] = positionKey;
    else delete next[playerId];
    saveArrangement.mutate({ formationKey, placements: next });
  };

  const sheet: SquadSheet = useMemo(
    () => ({
      slots: built.slots.map((s) => ({
        positionKey: s.positionKey,
        x: s.x,
        y: s.y,
        unit: s.unit,
        players: s.players.map((p) => ({
          name: p.name,
          shirtNumber: p.shirtNumber,
          roleKey: p.roleKey,
        })),
      })),
      unplaced: built.unplaced.map((p) => ({
        name: p.name,
        shirtNumber: p.shirtNumber,
        positionKey: p.primaryPositionKey,
      })),
      unplacedLabel: t('formations.unplaced'),
      header: {
        title: t('report.squadPitch'),
        phaseLabel: org.data?.name ?? '',
        teamName: '',
        coachName: user?.name ?? '',
        formationKey,
        date: formatDate(todayISO(), locale),
      },
    }),
    [built, formationKey, locale, org.data?.name, t, user?.name],
  );

  useEffect(() => {
    const ctx = canvas.current?.getContext('2d');
    if (!ctx) return;
    drawSquadSheet(ctx, SHEET_WIDTH, SHEET_HEIGHT, sheet, dirFor(locale));
  }, [sheet, locale]);

  const runExport = async () => {
    const node = canvas.current;
    if (!node) return;
    setExporting(true);
    setExportError(null);
    try {
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      pdf.addImage(node.toDataURL('image/jpeg', 0.94), 'JPEG', 0, 0, 210, 297);
      pdf.save(`squad-${formationKey}-${todayISO()}.pdf`);
    } catch {
      setExportError(t('report.failed'));
    } finally {
      setExporting(false);
    }
  };

  if (players.isLoading) {
    return (
      <p className="flex items-center gap-2 text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }

  return (
    <Panel
      title={t('formations.viewSquad')}
      count={squad.length || undefined}
      action={
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-32">
            <SelectInput
              aria-label={t('depth.formation')}
              value={formationKey}
              onChange={(e) => setFormationKey(e.target.value)}
            >
              {FORMATIONS.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.key}
                </option>
              ))}
            </SelectInput>
          </div>
          <Button variant="secondary" onClick={() => window.print()}>
            {t('report.print')}
          </Button>
          <Button variant="primary" busy={exporting} onClick={() => void runExport()}>
            {exporting ? t('report.preparing') : t('report.export')}
          </Button>
        </div>
      }
    >
      <div className="grid gap-2 p-4">
        <p className="max-w-[72ch] text-meta text-ink-muted">{t('formations.squadFrom')}</p>
        {exportError ? <Notice tone="risk">{exportError}</Notice> : null}
        {squad.length === 0 ? (
          <EmptyState title={t('squad.empty')} body={t('squad.emptyBody')} />
        ) : (
          <>
            <div className="report-preview overflow-x-auto">
              <canvas
                ref={canvas}
                width={SHEET_WIDTH}
                height={SHEET_HEIGHT}
                className="mx-auto block h-auto w-full max-w-[52rem] rounded-(--radius-panel) border border-line bg-surface"
              />
            </div>

            {/* Not printed: this is how the page above is arranged, and it
                would be an odd thing to hand to a director. */}
            <details className="group mt-2 rounded-(--radius-panel) border border-line print:hidden">
              <summary className="flex min-h-14 cursor-pointer list-none items-center gap-3 px-4 py-3">
                <span className="font-condensed text-subhead font-semibold tracking-tight">
                  {t('formations.arrange')}
                </span>
                <span className="min-w-0 flex-1 truncate text-meta text-ink-faint group-open:hidden">
                  {t('formations.arrangeSummary', { count: Object.keys(placements).length })}
                </span>
                <span aria-hidden="true" className="ms-auto text-ink-faint transition-transform group-open:rotate-90">
                  ›
                </span>
              </summary>
              <div className="border-t border-line p-4">
                <p className="mb-3 max-w-[72ch] text-meta text-ink-muted">
                  {t('formations.arrangeHint')}
                </p>
                <ul className="grid gap-x-5 gap-y-1 sm:grid-cols-2">
                  {squad.map((p) => (
                    <li key={p.id} className="flex items-center gap-3 py-1">
                      <span className="min-w-0 flex-1 truncate">
                        <span className="tabular me-2 text-ink-faint">{p.shirtNumber ?? '–'}</span>
                        {p.name}
                      </span>
                      <span className="w-24 shrink-0">
                        <SelectInput
                          aria-label={t('formations.positionFor', { name: p.name })}
                          className="!px-2 text-center !text-meta"
                          value={placements[p.id] ?? ''}
                          onChange={(e) => place(p.id, e.target.value)}
                        >
                          <option value="">{t('formations.asWorkedOut')}</option>
                          {positions.map((key) => (
                            <option key={key} value={key}>
                              {key}
                            </option>
                          ))}
                        </SelectInput>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </details>
          </>
        )}
      </div>
    </Panel>
  );
}
